import React, { createContext, useContext, ReactNode } from 'react';
import { Language, Translations } from '../types/language';
import { getTranslation } from '../translations';
import { getLocaleFromPathname, getLocaleHref } from '../utils/localePath';

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const VALID_LANGUAGES: Language[] = ['ko', 'en', 'ja', 'cn'];

const isValidLanguage = (val: unknown): val is Language =>
  typeof val === 'string' && VALID_LANGUAGES.includes(val as Language);

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const currentLanguage = getLocaleFromPathname(window.location.pathname);

  const setLanguage = (language: Language) => {
    const validLang = isValidLanguage(language) ? language : 'ko';
    if (validLang === currentLanguage) {
      return;
    }
    localStorage.setItem('sejong-language', validLang);
    window.location.href = getLocaleHref(validLang);
  };

  const t = getTranslation(currentLanguage);

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
